import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Skeleton,
} from '@mui/material'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import axios from 'src/utilities/http-client'
import {
  LiveClassTotalsRequest,
  LiveClassTotalsResponse,
  SessionBreakdown,
} from '../scribe.slice'

interface AttendanceBreakdownProps {
  courseCode: string
  startDate: string
  endDate: string
}

let getLicenseColor = (
  license: string,
): 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
  switch (license.toLowerCase()) {
    case 'nurse':
      return 'primary'
    case 'paramedic':
      return 'success'
    case 'emt':
      return 'info'
    case 'aemt':
      return 'warning'
    case 'other':
      return 'secondary'
    default:
      return 'secondary'
  }
}

let formatLicenseLabel = (license: string): string => {
  switch (license.toLowerCase()) {
    case 'nurse':
      return 'Nurses'
    case 'paramedic':
      return 'Paramedics'
    case 'emt':
      return 'EMTs'
    case 'aemt':
      return 'AEMTs'
    case 'other':
      return 'Others'
    default:
      return license
  }
}

let SessionBreakdownCard = ({ session }: { session: SessionBreakdown }) => {
  let sessionDate = dayjs.utc(session.sessionTime).local().format('MMM D, YYYY')
  let sessionTime = dayjs.utc(session.sessionTime).local().format('h:mm A')

  let licenseEntries = Object.entries(session.licenseBreakdown).map(
    ([license, count]) => ({
      license,
      count,
      percentage:
        session.totalAttendees > 0
          ? ((count / session.totalAttendees) * 100).toFixed(1)
          : '0.0',
    }),
  )

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              {session.className}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {sessionDate} at {sessionTime}
            </Typography>
          </Box>
          <Box
            sx={{
              textAlign: 'right',
            }}
          >
            <Typography color="primary" variant="h5">
              {session.totalAttendees}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              attendees
            </Typography>
          </Box>
        </Box>

        <div className="flex flex-wrap gap-2">
          {licenseEntries.map(({ license, count, percentage }) => (
            <div
              key={license}
              className="w-[calc(50%-0.25rem)] sm:w-[calc(33.33%-0.34rem)] md:w-[calc(20%-0.4rem)]"
            >
              <Box
                sx={{
                  bgcolor: 'background.paperLight',
                  borderRadius: 1,
                  textAlign: 'center',
                  p: 1.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: `${getLicenseColor(license)}.main`,
                  }}
                >
                  {count}
                </Typography>
                <Chip
                  label={formatLicenseLabel(license)}
                  size="small"
                  sx={{
                    color: getLicenseColor(license),
                    mb: 0.5,
                    fontSize: '0.7rem',
                  }}
                />
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{
                    display: 'block',
                  }}
                >
                  {percentage}%
                </Typography>
              </Box>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

export default ({
  courseCode,
  startDate,
  endDate,
}: AttendanceBreakdownProps) => {
  let [totalsData, setTotalsData] = useState<LiveClassTotalsResponse | null>(
    null,
  )
  let [loading, setLoading] = useState(false)
  let [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let fetchTotals = async () => {
      if (!courseCode || !startDate || !endDate) {
        setTotalsData(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        let request: LiveClassTotalsRequest = {
          courseCode,
          startDate: dayjs(startDate).format('YYYY-MM-DD'),
          endDate: dayjs(endDate).format('YYYY-MM-DD'),
        }

        let response = await axios.post<LiveClassTotalsResponse>(
          '/api/scribe/live-class/totals',
          request,
        )

        setTotalsData(response.data)
      } catch (err: any) {
        let errorMsg =
          err.response?.data?.error || 'Failed to fetch attendance breakdown'
        setError(errorMsg)
      } finally {
        setLoading(false)
      }
    }

    fetchTotals()
  }, [courseCode, startDate, endDate])

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Skeleton
            variant="text"
            sx={{
              width: '40%',
              height: 32,
              mb: 2,
            }}
          />
          <Skeleton
            variant="text"
            sx={{
              width: '60%',
              height: 24,
              mb: 3,
            }}
          />

          {/* License breakdown skeleton */}
          <div className="flex flex-wrap gap-4">
            {[1, 2, 3, 4, 5].map((item) => (
              <div
                key={item}
                className="w-full sm:w-[calc(50%-0.5rem)] md:w-[calc(20%-0.8rem)]"
              >
                <Box
                  sx={{
                    bgcolor: 'background.paperDark',
                    borderRadius: 1,
                    textAlign: 'center',
                    p: 2,
                  }}
                >
                  <Skeleton
                    variant="text"
                    sx={{
                      width: '60%',
                      height: 32,
                      mx: 'auto',
                    }}
                  />
                  <Skeleton
                    variant="text"
                    sx={{
                      width: '80%',
                      height: 20,
                      mx: 'auto',
                    }}
                  />
                </Box>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Attendance Breakdown
          </Typography>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    )
  }

  if (!totalsData) {
    return null
  }

  let { licenseBreakdown, totalAttendees, sessionBreakdowns } = totalsData

  let licenseEntries = Object.entries(licenseBreakdown).map(
    ([license, count]) => ({
      license,
      count,
      percentage:
        totalAttendees > 0
          ? ((count / totalAttendees) * 100).toFixed(1)
          : '0.0',
    }),
  )

  return (
    <Box>
      {/* Overall Course Breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Attendance Breakdown
          </Typography>

          <Typography color="text.secondary" variant="subtitle1" gutterBottom>
            {totalsData.courseName} - {totalsData.dateRange}
          </Typography>

          <Box
            sx={{
              mb: 2,
            }}
          >
            <Typography color="primary" variant="h4" component="span">
              {totalAttendees}
            </Typography>
            <Typography
              color="text.secondary"
              variant="body1"
              component="span"
              sx={{
                ml: 1,
              }}
            >
              Total Attendees
            </Typography>
          </Box>

          {/* Overall License Breakdown */}
          <div className="flex flex-wrap gap-4">
            {licenseEntries.map(({ license, count, percentage }) => (
              <div
                key={license}
                className="w-full sm:w-[calc(50%-0.5rem)] md:w-[calc(20%-0.8rem)]"
              >
                <Box
                  sx={{
                    bgcolor: 'background.paperDark',
                    borderRadius: 1,
                    textAlign: 'center',
                    p: 2,
                    height: '100%',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: `${getLicenseColor(license)}.main`,
                    }}
                  >
                    {count}
                  </Typography>
                  <Chip
                    label={formatLicenseLabel(license)}
                    size="small"
                    sx={{
                      color: getLicenseColor(license),
                      mb: 1,
                    }}
                  />
                  <Typography
                    color="text.secondary"
                    variant="caption"
                    sx={{
                      display: 'block',
                    }}
                  >
                    {percentage}% of total
                  </Typography>
                </Box>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      {/* Individual Session Breakdowns */}
      {sessionBreakdowns && sessionBreakdowns.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Individual Sessions ({sessionBreakdowns.length} sessions)
          </Typography>
          {
            // eslint-disable-next-line fp/no-mutating-methods
            sessionBreakdowns
              .sort(
                (a, b) =>
                  dayjs.utc(a.sessionTime).unix() -
                  dayjs.utc(b.sessionTime).unix(),
              )
              .map((session) => (
                <SessionBreakdownCard
                  key={session.sessionId}
                  session={session}
                />
              ))
          }
        </Box>
      )}
    </Box>
  )
}
